<template>
  <div class="routing-panel">
    <h3>Route Planner</h3>
    
    <!-- Solver Type Selection -->
    <div class="solver-type-toggle">
      <button 
        @click="setSolverType('VRP')" 
        :class="{ active: solverType === 'VRP' }"
      >VRP</button>
      <button 
        @click="setSolverType('TSP')" 
        :class="{ active: solverType === 'TSP' }"
      >TSP</button>
      <button 
        @click="setSolverType('LAP')" 
        :class="{ active: solverType === 'LAP' }"
      >LAP</button>
    </div>
    
    <div class="location-source-toggle">
      <button 
        @click="setLocationSource('map')" 
        :class="{ active: locationSource === 'map' }"
      >Map</button>
      <button 
        @click="setLocationSource('database')" 
        :class="{ active: locationSource === 'database' }"
      >DB</button>
      <button 
        @click="setLocationSource('collections')" 
        :class="{ active: locationSource === 'collections' }"
      >Sets</button>
    </div>

    <!-- TSP Mode for User-Defined Depots and Vehicles -->
    <div v-if="solverType === 'TSP'" class="tsp-mode">
      <div class="modern-vehicle-config">
        <div class="config-header">
          <h4>🚛 Vehicle Configuration</h4>
          <p class="config-subtitle">Configure your fleet for optimal routing</p>
        </div>
        
        <div class="config-grid">
          <div class="config-item">
            <div class="modern-input-wrapper">
              <input 
                type="number" 
                v-model.number="numVehicles" 
                min="1" 
                max="10" 
                class="modern-input"
                placeholder="2"
              />
              <div class="input-suffix">vehicles</div>
            </div>
          </div>
          
          <div class="config-item">
            <div class="modern-input-wrapper">
              <input 
                type="number" 
                v-model.number="vehicleCapacity" 
                min="1" 
                max="1000" 
                class="modern-input"
                placeholder="100"
              />
              <div class="input-suffix">units</div>
            </div>
            <small class="modern-help">Capacity applies to all vehicles</small>
          </div>
        </div>
      </div>
      
      <!-- Show depot selection for database/collections mode -->
      <div v-if="locationSource === 'database' || locationSource === 'collections'" class="form-group">
        <label>Select Depots</label>
        <div v-if="isLoadingLocations" class="loading-message">Loading depots...</div>
        <div v-else-if="depotLocations.length === 0" class="empty-message">
          No depots available. Please add depots in the Locations section.
        </div>
        <div v-else class="depot-selection">
          <div v-for="depot in sortedDepotLocations" :key="depot.id" class="depot-checkbox" :class="{ 'selected-item': selectedDepotIds.includes(depot.id) }">
            <input 
              type="checkbox" 
              :id="'tsp-depot-' + depot.id" 
              :value="depot.id" 
              v-model="selectedDepotIds"
            />
            <label :for="'tsp-depot-' + depot.id">
              <span v-if="selectedDepotIds.includes(depot.id)" class="selected-indicator">✓</span>
              {{ depot.name }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Collection Selection -->
    <div v-if="locationSource === 'collections'" class="collection-selection">
      <div class="form-group">
        <label>Select Collection</label>
        <div v-if="isLoadingCollections" class="loading-message">Loading collections...</div>
        <div v-else-if="availableCollections.length === 0" class="empty-message">
          No collections available. Please create collections in the Locations section.
        </div>
        <select v-else v-model="selectedCollectionId" @change="onCollectionChanged" class="collection-dropdown">
          <option value="">Select a collection...</option>
          <option v-for="collection in availableCollections" :key="collection.id" :value="collection.id">
            {{ collection.name }} ({{ collection.location_count }} locations)
          </option>
        </select>
      </div>
    </div>

    <!-- Database Locations Selection -->
    <div v-if="locationSource === 'database' || locationSource === 'collections'" class="database-locations">
      <div v-if="solverType === 'VRP' || solverType === 'LAP'" class="form-group">
        <label>{{ solverType === 'LAP' ? 'Select Facilities' : 'Select Depots' }}</label>
        <div v-if="isLoadingLocations" class="loading-message">{{ solverType === 'LAP' ? 'Loading facilities...' : 'Loading depots...' }}</div>
        <div v-else-if="depotLocations.length === 0" class="empty-message">
          {{ solverType === 'LAP' ? 'No facilities available. Please add facilities in the Locations section.' : 'No depots available. Please add depots in the Locations section.' }}
        </div>
        <div v-else class="depot-selection">
          <div v-for="depot in sortedDepotLocations" :key="depot.id" class="depot-checkbox" :class="{ 'selected-item': selectedDepotIds.includes(depot.id) }">
            <input 
              type="checkbox" 
              :id="'depot-' + depot.id" 
              :value="depot.id" 
              v-model="selectedDepotIds"
            />
            <label :for="'depot-' + depot.id">
              <span v-if="selectedDepotIds.includes(depot.id)" class="selected-indicator">✓</span>
              {{ depot.name }}
              <span v-if="solverType === 'LAP' && depot.capacity" class="capacity-value">(Capacity: {{ depot.capacity }})</span>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>Select Demand Points</label>
        <div v-if="isLoadingLocations" class="loading-message">Loading demand points...</div>
        <div v-else-if="demandLocations.length === 0" class="empty-message">
          No demand points available. Please add demand points in the Locations section.
        </div>
        <div v-else class="demand-selection">
          <div v-for="location in sortedDemandLocations" :key="location.id" class="demand-checkbox" :class="{ 'selected-item': selectedDemandIds.includes(location.id) }">
            <input 
              type="checkbox" 
              :id="'demand-' + location.id" 
              :value="location.id" 
              v-model="selectedDemandIds"
            />
            <label :for="'demand-' + location.id">
              <span v-if="selectedDemandIds.includes(location.id)" class="selected-indicator">✓</span>
              {{ location.name }} 
              <span v-if="location.demand" class="demand-value">(Demand: {{ location.demand }})</span>
            </label>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Map Locations (Current Implementation) -->
    <div v-else>
      <div class="locations-list">
        <h4>Locations</h4>
        <p v-if="!locations.length">No locations added yet. Click on the map to add locations.</p>
        
        <div v-for="(location, index) in locations" :key="index" class="location-item">
          <span>Location {{ index + 1 }}</span>
          <div class="location-coords">
            {{ location[0].toFixed(4) }}, {{ location[1].toFixed(4) }}
          </div>
          <div v-if="usesDemands" class="location-demand">
            Demand: <input type="number" v-model.number="demands[index]" min="1" class="small-input" />
          </div>
          <button @click="removeLocation(index)" class="remove-btn">Remove</button>
        </div>
      </div>
    </div>
    
    <!-- LAP-specific Options -->
    <div v-if="solverType === 'LAP'" class="lap-options">
      <div class="form-group">
        <label>Optimization Objective</label>
        <select v-model="lapObjective" class="objective-dropdown">
          <option value="minimize_cost">Minimize Total Cost</option>
          <option value="minimize_distance">Minimize Total Distance</option>
          <option value="maximize_coverage">Maximize Coverage</option>
        </select>
      </div>
      
      <div class="form-group">
        <label>Maximum Assignment Distance (km)</label>
        <input 
          type="number" 
          v-model.number="lapMaxDistance" 
          min="1" 
          max="1000" 
          step="1"
          class="distance-input"
          placeholder="50"
        />
      </div>
      
      <div class="form-group">
        <label>Cost per Kilometer</label>
        <input 
          type="number" 
          v-model.number="lapCostPerKm" 
          min="0.001" 
          max="10" 
          step="0.001"
          class="cost-input"
          placeholder="1.0"
        />
      </div>
    </div>

    <!-- Vehicle Selection (Hidden for TSP and LAP) -->
    <div v-if="solverType === 'VRP'" class="form-group">
      <label>Select Vehicles</label>
      <div v-if="isLoadingVehicles" class="loading-message">Loading vehicles...</div>
      <div v-else-if="availableVehicles.length === 0" class="empty-message">
        No vehicles available. Please add vehicles in the Vehicles section.
      </div>
      <div v-else class="vehicles-selection">
        <div v-for="vehicle in sortedVehicles" :key="vehicle.id" class="vehicle-checkbox" :class="{ 'selected-item': selectedVehicleIds.includes(vehicle.id) }">
          <input 
            type="checkbox" 
            :id="'vehicle-' + vehicle.id" 
            :value="vehicle.id" 
            v-model="selectedVehicleIds"
          />
          <label :for="'vehicle-' + vehicle.id">
            <span v-if="selectedVehicleIds.includes(vehicle.id)" class="selected-indicator">✓</span>
            {{ vehicle.plate }} ({{ vehicle.type }}) - Capacity: {{ vehicle.capacity }}
          </label>
        </div>
      </div>
    </div>
    
    <div class="form-actions">
      <button 
        v-if="locationSource === 'map'" 
        @click="toggleDemands" 
        class="secondary-btn"
      >
        {{ usesDemands ? 'Disable Demands' : 'Enable Demands' }}
      </button>
      <button 
        v-if="locationSource === 'map'" 
        @click="clearLocations" 
        class="secondary-btn"
      >Clear All</button>
      <button 
        @click="solveRoutes" 
        class="primary-btn" 
        :disabled="!canSolve"
      >{{ solverType === 'TSP' ? 'Assign Service Personnel' : solverType === 'LAP' ? 'Solve LAP' : 'Solve Routes' }}</button>
      <!-- Save Route Button: Only show when a solution exists -->
      <button
        v-if="solution"
        @click="saveRouteDialog"
        class="primary-btn"
        style="margin-left: 12px;"
      >Save Route</button>
    </div>
    <!-- Save Route Dialog -->
    <div v-if="showSaveDialog" class="save-route-dialog">
      <div class="dialog-content">
        <label for="routeName">Route Name:</label>
        <input id="routeName" v-model="saveRouteName" placeholder="Enter route name" />
        <div class="dialog-actions">
          <button class="primary-btn" @click="confirmSaveRoute">Save</button>
          <button class="secondary-btn" @click="cancelSaveRoute">Cancel</button>
        </div>
      </div>
    </div>
    <div v-if="saveRouteError" class="error-message">{{ saveRouteError }}</div>
    <div v-if="saveRouteSuccess" class="success-message">{{ saveRouteSuccess }}</div>
    
    <div v-if="isLoading" class="loading">
      <p>Solving route optimization...</p>
    </div>
    
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    
    <div v-if="solution" class="solution-summary">
      <h4>Solution Found</h4>
      <p><strong>Total Distance:</strong> {{ (solution.total_distance / 1000).toFixed(2) }} km</p>
      <p v-if="solverType === 'VRP'"><strong>Routes:</strong> {{ solution.routes.length }}</p>
      <p v-if="solverType === 'TSP'"><strong>Route:</strong> Single optimal route</p>
      
      <!-- VRP Solution Display -->
      <template v-if="solverType === 'VRP'">
        <div v-for="(route, index) in solution.routes" :key="'route'+index" class="route-item">
        <div class="route-header" :style="{backgroundColor: routeColors[index]}">
          <div>{{ getVehicleInfo(route.vehicle) }}</div>
          <div v-if="route.vehicleId" class="route-capacity">
            <span>{{ getVehicleCapacity(route.vehicleId) }}</span>
          </div>
        </div>
        <div class="route-stops">
          <span v-for="(stop, stopIndex) in route.stops" :key="'stop'+stopIndex" class="route-stop">
            {{ stopToLabel(stop) }}
            <span v-if="stopIndex < route.stops.length - 1" class="route-arrow">→</span>
          </span>
        </div>
        </div>
      </template>
      
      <!-- TSP Solution Display -->
      <div v-if="solverType === 'TSP'" class="route-item">
        <div class="route-header" style="background-color: #4285F4;">
          <div>TSP Route</div>
          <div class="route-duration">
            Duration: {{ solution.route ? solution.route.duration_formatted : solution.total_duration_formatted }}
          </div>
        </div>
        <div class="route-stops">
          <span v-for="(stop, stopIndex) in (solution.route ? solution.route.stops : [])" :key="'stop'+stopIndex" class="route-stop">
            {{ stopToLabel(stop) }}
            <span v-if="stopIndex < (solution.route ? solution.route.stops.length : 0) - 1" class="route-arrow">→</span>
          </span>
        </div>
      </div>
      
      <!-- LAP Solution Display -->
      <div v-if="solverType === 'LAP'" class="lap-solution">
        <div class="solution-metrics">
          <div class="metric-item">
            <span class="metric-label">Total Cost:</span>
            <span class="metric-value">{{ solution.total_cost?.toFixed(2) || 'N/A' }}</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Avg Distance:</span>
            <span class="metric-value">{{ (solution.average_distance / 1000)?.toFixed(2) || 'N/A' }} km</span>
          </div>
          <div class="metric-item">
            <span class="metric-label">Assignments:</span>
            <span class="metric-value">{{ solution.num_assignments || 0 }}</span>
          </div>
        </div>
        
        <div v-if="solution.facility_utilization" class="facility-utilization">
          <h5>Facility Utilization</h5>
          <div v-for="(utilization, facilityIdx) in solution.facility_utilization" :key="'util-' + facilityIdx" class="utilization-item">
            <div class="facility-name">
              {{ getFacilityName(facilityIdx) }}
            </div>
            <div class="utilization-bar">
              <div class="utilization-fill" :style="{ width: (utilization.utilization_rate * 100) + '%', backgroundColor: getFacilityColor(facilityIdx) }"></div>
            </div>
            <div class="utilization-text">
              {{ (utilization.utilization_rate * 100).toFixed(1) }}% 
              ({{ utilization.load }}/{{ utilization.capacity }})
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RoutingService from '@/services/RoutingService';
import VehiclesService from '@/services/VehiclesService';
import LocationsService from '@/services/LocationsService';

export default {
  name: 'RoutingPanel',
  data() {
    return {
      // Solver type selection
      solverType: 'VRP', // 'VRP', 'TSP', or 'LAP'
      
      // Service personnel parameters
      numServicePersonnel: 3, // Number of salespersons for simplified mode
      
      // Vehicle parameters
      availableVehicles: [],
      selectedVehicleIds: [],
      isLoadingVehicles: false,
      
      // Locations parameters
      locationSource: 'map', // 'map', 'database', or 'collections'
      depotLocations: [],
      demandLocations: [],
      selectedDepotIds: [], // Array to support multiple depots
      selectedDemandIds: [],
      isLoadingLocations: false,
      
      // Collections parameters
      availableCollections: [],
      selectedCollectionId: '',
      isLoadingCollections: false,
      
      // Old parameters (kept for compatibility)
      numVehicles: 3,
      vehicleCapacity: 10,
      usesDemands: false,
      
      // Locations data
      depot: [0, 0], // Default depot location
      locations: [], // Array of [lon, lat] locations
      demands: [],   // Array of demands for each location
      
      // UI state
      isLoading: false,
      error: null,
      solution: null,
      routeColors: [],
      
      // LAP-specific parameters
      lapObjective: 'minimize_cost',
      lapMaxDistance: 50, // kilometers
      lapCostPerKm: 1.0,
      
      // Save route parameters
      showSaveDialog: false,
      saveRouteName: '',
      saveRouteError: '',
      saveRouteSuccess: '',
    };
  },
  computed: {
    canSolve() {
      if (this.locationSource === 'map') {
        if (this.solverType === 'TSP') {
          return this.locations.length > 0 && this.numVehicles > 0 && this.vehicleCapacity > 0;
        } else if (this.solverType === 'LAP') {
          return this.locations.length > 0;
        } else {
          return this.locations.length > 0 && this.getSelectedVehicles().length > 0;
        }
      } else if (this.locationSource === 'collections') {
        if (this.solverType === 'TSP') {
          // TSP mode - need depots, demand points, vehicle count and capacity
          return this.selectedCollectionId && this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0 && this.numVehicles > 0 && this.vehicleCapacity > 0;
        } else if (this.solverType === 'LAP') {
          return this.selectedCollectionId && this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0;
        } else {
          return this.selectedCollectionId && this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0 && this.getSelectedVehicles().length > 0;
        }
      } else {
        if (this.solverType === 'TSP') {
          // TSP mode - need depots, demand points, vehicle count and capacity
          return this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0 && this.numVehicles > 0 && this.vehicleCapacity > 0;
        } else if (this.solverType === 'LAP') {
          return this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0;
        } else {
          return this.selectedDepotIds.length > 0 && this.selectedDemandIds.length > 0 && this.getSelectedVehicles().length > 0;
        }
      }
    },
    
    
    // All locations including the depot(s)
    allLocations() {
      if (this.locationSource === 'map') {
        return [this.depot, ...this.locations];
      } else if (this.solution) {
        // When we have a solution, get locations from the solution
        return this.solution.locations;
      } else {
        return [];
      }
    },
    
    // Sorted depot locations with selected items first
    sortedDepotLocations() {
      return [...this.depotLocations].sort((a, b) => {
        const aSelected = this.selectedDepotIds.includes(a.id);
        const bSelected = this.selectedDepotIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.name.localeCompare(b.name);
      });
    },
    
    // Sorted demand locations with selected items first
    sortedDemandLocations() {
      return [...this.demandLocations].sort((a, b) => {
        const aSelected = this.selectedDemandIds.includes(a.id);
        const bSelected = this.selectedDemandIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.name.localeCompare(b.name);
      });
    },
    
    // Sorted vehicles with selected items first
    sortedVehicles() {
      return [...this.availableVehicles].sort((a, b) => {
        const aSelected = this.selectedVehicleIds.includes(a.id);
        const bSelected = this.selectedVehicleIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.plate.localeCompare(b.plate);
      });
    }
  },
  mounted() {
    // Load vehicles, locations, and collections when the component is mounted
    this.loadVehicles();
    this.loadLocations();
    this.loadCollections();
  },
  methods: {
    /**
     * Load available vehicles from the backend
     */
    loadVehicles() {
      this.isLoadingVehicles = true;
      
      VehiclesService.getVehicles()
        .then(response => {
          this.availableVehicles = response.data;
          // Pre-select the first vehicle if any are available
          if (this.availableVehicles.length > 0) {
            this.selectedVehicleIds = [this.availableVehicles[0].id];
          }
          this.isLoadingVehicles = false;
        })
        .catch(error => {
          console.error('Error loading vehicles:', error);
          this.isLoadingVehicles = false;
        });
    },
    
    /**
     * Load locations from the backend
     */
    loadLocations() {
      if (this.locationSource === 'collections' && this.selectedCollectionId) {
        this.loadCollectionLocations();
      } else {
        this.loadDatabaseLocations();
      }
    },
    
    /**
     * Load locations from regular database
     */
    loadDatabaseLocations() {
      this.isLoadingLocations = true;
      
      // Load depots
      LocationsService.getDepots()
        .then(response => {
          this.depotLocations = response.data;
          // Pre-select the first depot if any are available
          if (this.depotLocations.length > 0) {
            this.selectedDepotIds = [this.depotLocations[0].id];
          }
        })
        .catch(error => {
          console.error('Error loading depots:', error);
        });
      
      // Load demand points
      LocationsService.getDemandPoints()
        .then(response => {
          this.demandLocations = response.data;
          this.isLoadingLocations = false;
        })
        .catch(error => {
          console.error('Error loading demand points:', error);
          this.isLoadingLocations = false;
        });
    },
    
    /**
     * Load locations from a specific collection
     */
    loadCollectionLocations() {
      if (!this.selectedCollectionId) return;
      
      this.isLoadingLocations = true;
      this.selectedDepotIds = [];
      this.selectedDemandIds = [];
      
      // Load depots from collection
      LocationsService.getCollectionDepots(this.selectedCollectionId)
        .then(response => {
          this.depotLocations = response.data;
          // Pre-select the first depot if any are available
          if (this.depotLocations.length > 0) {
            this.selectedDepotIds = [this.depotLocations[0].id];
          }
        })
        .catch(error => {
          console.error('Error loading collection depots:', error);
        });
      
      // Load demand points from collection
      LocationsService.getCollectionDemandPoints(this.selectedCollectionId)
        .then(response => {
          this.demandLocations = response.data;
          this.isLoadingLocations = false;
        })
        .catch(error => {
          console.error('Error loading collection demand points:', error);
          this.isLoadingLocations = false;
        });
    },
    
    /**
     * Load available collections
     */
    loadCollections() {
      this.isLoadingCollections = true;
      
      LocationsService.getCollections()
        .then(response => {
          this.availableCollections = response.data;
          this.isLoadingCollections = false;
        })
        .catch(error => {
          console.error('Error loading collections:', error);
          this.isLoadingCollections = false;
        });
    },
    
    /**
     * Set solver type and handle transitions
     */
    setSolverType(type) {
      this.solverType = type;
      // Clear any existing solution when switching solver types
      this.solution = null;
      this.error = null;
      this.routeColors = [];
      
      // Emit solution cleared event to update map
      this.$emit('solution-found', {
        solution: null,
        allLocations: [],
        routeColors: [],
        solverType: this.solverType
      });
    },
    
    /**
     * Set location source and handle transitions
     */
    setLocationSource(source) {
      this.locationSource = source;
      
      // Clear selections when switching sources
      this.selectedDepotIds = [];
      this.selectedDemandIds = [];
      this.depotLocations = [];
      this.demandLocations = [];
      
      // Clear solution and problem data when switching sources
      this.solution = null;
      this.error = null;
      this.routeColors = [];
      
      if (source === 'collections') {
        this.selectedCollectionId = '';
        // Collections are already loaded in mounted()
      } else if (source === 'database') {
        this.loadDatabaseLocations();
      }
      // Map source doesn't need additional loading
      
      // Emit solution cleared event to update map
      this.$emit('solution-found', {
        solution: null,
        allLocations: [],
        routeColors: [],
        solverType: this.solverType
      });
      
      // Emit location source change event
      this.$emit('location-source-changed', {
        source: source,
        collectionId: this.selectedCollectionId
      });
    },
    
    /**
     * Handle collection selection change
     */
    onCollectionChanged() {
      if (this.selectedCollectionId) {
        this.loadCollectionLocations();
      } else {
        // Clear location data when no collection is selected
        this.selectedDepotIds = [];
        this.selectedDemandIds = [];
        this.depotLocations = [];
        this.demandLocations = [];
      }
      
      // Emit location source change event with updated collection
      this.$emit('location-source-changed', {
        source: this.locationSource,
        collectionId: this.selectedCollectionId
      });
    },
    
    /**
     * Get the currently selected vehicles as objects
     */
    getSelectedVehicles() {
      return this.availableVehicles.filter(vehicle => 
        this.selectedVehicleIds.includes(vehicle.id)
      );
    },
    
    /**
     * Set the depot location
     */
    setDepot(coordinates) {
      this.depot = coordinates;
    },
    
    /**
     * Add a location to visit
     */
    addLocation(coordinates) {
      this.locations.push(coordinates);
      this.demands.push(1); // Default demand
    },
    
    /**
     * Remove a location
     */
    removeLocation(index) {
      this.locations.splice(index, 1);
      this.demands.splice(index, 1);
    },
    
    /**
     * Clear all locations
     */
    clearLocations() {
      this.locations = [];
      this.demands = [];
      this.solution = null;
      this.error = null;
      this.routeColors = [];
      
      // Also clear selected locations for database/collections mode
      this.selectedDepotIds = [];
      this.selectedDemandIds = [];
      this.selectedVehicleIds = [];
      
      // Emit solution cleared event to update map
      this.$emit('solution-found', {
        solution: null,
        allLocations: [],
        routeColors: [],
        solverType: this.solverType
      });
    },
    
    /**
     * Toggle using demands
     */
    toggleDemands() {
      this.usesDemands = !this.usesDemands;
    },
    
    /**
     * Validate problem data before solving
     */
    validateProblemData(problemData) {
      // Only check vehicles for VRP, not TSP
      if (this.solverType === 'VRP') {
        if (!problemData.vehicles || problemData.vehicles.length === 0) {
          return { isValid: false, error: "No vehicles selected" };
        }
      }
      
      if (this.locationSource === 'map') {
        if (!problemData.locations || problemData.locations.length === 0) {
          return { isValid: false, error: "No locations to visit" };
        }
      } else {
        if (this.solverType !== 'TSP' && this.selectedDepotIds.length === 0) {
          return { isValid: false, error: "No depots selected" };
        }
        if (this.selectedDemandIds.length === 0) {
          return { isValid: false, error: "No demand points selected" };
        }
        if (this.solverType === 'TSP') {
          if (this.selectedDepotIds.length === 0) {
            return { isValid: false, error: "At least one depot is required for TSP mode" };
          }
          if (!this.numVehicles || this.numVehicles <= 0) {
            return { isValid: false, error: "Number of vehicles is required for TSP mode" };
          }
          if (!this.vehicleCapacity || this.vehicleCapacity <= 0) {
            return { isValid: false, error: "Vehicle capacity is required for TSP mode" };
          }
        }
      }
      
      return { isValid: true };
    },
    
    /**
     * Solve the VRP, TSP, or LAP problem
     */
    async solveRoutes() {
      console.log('canSolve:', this.canSolve, 'solverType:', this.solverType);
      if (!this.canSolve) return;
      
      this.isLoading = true;
      this.error = null;
      
      try {
        // For VRP, check vehicle selection
        if (this.solverType === 'VRP') {
          const selectedVehicles = this.getSelectedVehicles();
          
          if (selectedVehicles.length === 0) {
            this.error = "Please select at least one vehicle";
            this.isLoading = false;
            return;
          }
        }
        
        // Prepare the problem data
        let problemData = {};
        
        // Add vehicle data for VRP and TSP
        if (this.solverType === 'VRP') {
          // Standard VRP mode: use selected vehicles
          const selectedVehicles = this.getSelectedVehicles();
          problemData.vehicles = selectedVehicles;
          problemData.numVehicles = selectedVehicles.length;
        } else if (this.solverType === 'TSP') {
          // TSP mode: use user-specified vehicle count and capacity
          problemData.numVehicles = this.numVehicles;
          problemData.vehicleCapacity = this.vehicleCapacity;
        }
        
        // Add LAP-specific parameters
        if (this.solverType === 'LAP') {
          problemData.objective = this.lapObjective;
          problemData.algorithm = 'assignment';
          problemData.max_distance = this.lapMaxDistance * 1000; // Convert km to meters
          problemData.cost_per_meter = this.lapCostPerKm / 1000; // Convert cost per km to cost per meter
        }
        
        if (this.locationSource === 'map') {
          // Use map-based locations
          if (this.solverType === 'TSP') {
            // For TSP in map mode, user needs to set depot and locations through map clicks
            // First location is treated as depot, rest as demand points
            if (this.locations.length === 0) {
              this.error = "Please add locations on the map. First location will be used as depot.";
              this.isLoading = false;
              return;
            }
            
            problemData.depots = [this.locations[0]]; // First location as depot
            problemData.locations = this.locations.slice(1); // Rest as demand points
            problemData.demands = this.demands.slice(1); // Corresponding demands
          } else {
            // Standard VRP/LAP mode
            problemData.depot = this.depot;
            problemData.locations = this.locations;
            problemData.multiDepot = false;
            
            // Add optional parameters for map-based locations
            if (this.usesDemands) {
              // Use the capacities of the selected vehicles
              const selectedVehicles = this.getSelectedVehicles();
              problemData.vehicleCapacities = selectedVehicles.map(v => v.capacity);
              problemData.demands = [0, ...this.demands]; // 0 demand at depot
            }
          }
        } else {
          // Use database locations or collections with support for multiple depots
          // Get the selected depots/facilities
          const selectedDepots = this.depotLocations.filter(d => 
            this.selectedDepotIds.includes(d.id)
          );
          
          if (selectedDepots.length === 0 && this.solverType !== 'LAP') {
            this.error = this.solverType === 'TSP' ? "Please select at least one depot" : "Please select at least one depot";
            this.isLoading = false;
            return;
          }
          
          // Get the selected demand points
          const selectedDemands = this.demandLocations.filter(d => 
            this.selectedDemandIds.includes(d.id)
          );
          
          if (selectedDemands.length === 0) {
            this.error = "Please select at least one demand point";
            this.isLoading = false;
            return;
          }
          
          if (this.solverType === 'LAP') {
            // LAP-specific data format: facilities and demands
            problemData.facilities = selectedDepots.map(depot => ({
              id: depot.id,
              name: depot.name,
              coordinates: depot.coordinates,
              capacity: depot.capacity || 1000
            }));
            
            problemData.demands = selectedDemands.map(demand => ({
              id: demand.id,
              name: demand.name,
              coordinates: demand.coordinates,
              demand: demand.demand || 1
            }));
            
            console.log('LAP problem data:', {
              facilities: problemData.facilities,
              demands: problemData.demands,
              objective: problemData.objective,
              max_distance: problemData.max_distance
            });
          } else if (this.solverType === 'TSP') {
            // TSP mode: use user-defined depots and demand points
            const selectedDepots = this.depotLocations.filter(d => 
              this.selectedDepotIds.includes(d.id)
            );
            
            if (selectedDepots.length === 0) {
              this.error = "Please select at least one depot for TSP mode";
              this.isLoading = false;
              return;
            }
            
            problemData.depots = selectedDepots.map(d => d.coordinates);
            problemData.locations = selectedDemands.map(d => d.coordinates);
            problemData.demands = selectedDemands.map(d => d.demand || 1);
            problemData.demandNames = selectedDemands.map(d => d.name);
            problemData.depotNames = selectedDepots.map(d => d.name);
            
            // Add time windows from moventa.locations
            problemData.time_windows = this.extractTimeWindows(selectedDepots, selectedDemands);

            console.log('TSP mode problem data:', {
              depots: problemData.depots,
              locations: problemData.locations,
              demands: problemData.demands,
              time_windows: problemData.time_windows,
              numVehicles: this.numVehicles,
              vehicleCapacity: this.vehicleCapacity
            });
          } else {
            if (selectedDepots.length === 1) {
            // Single depot - use existing format
            problemData.depot = selectedDepots[0].coordinates;
            problemData.locations = selectedDemands.map(d => d.coordinates);
            problemData.multiDepot = false;
            if (this.solverType === 'VRP') {
              const selectedVehicles = this.getSelectedVehicles();
              problemData.vehicleCapacities = selectedVehicles.map(v => v.capacity);
            }
            problemData.demands = [0, ...selectedDemands.map(d => d.demand || 1)];
            problemData.demandNames = selectedDemands.map(d => d.name);
            problemData.depotName = selectedDepots[0].name;
            
            // Add time windows from moventa.locations
            problemData.time_windows = this.extractTimeWindows(selectedDepots, selectedDemands);

            console.log('Single depot problem data:', {
              depot: problemData.depot,
              locations: problemData.locations,
              demands: problemData.demands,
              time_windows: problemData.time_windows,
              demandNames: problemData.demandNames,
              selectedDemands: selectedDemands.map(d => ({ name: d.name, demand: d.demand }))
            });
          } else {
            // Multiple depots - use new multi-depot format
            problemData.multiDepot = true;
            problemData.depots = selectedDepots.map(d => d.coordinates);
            problemData.locations = selectedDemands.map(d => d.coordinates);
            if (this.solverType === 'VRP') {
              const selectedVehicles = this.getSelectedVehicles();
              problemData.vehicleCapacities = selectedVehicles.map(v => v.capacity);
            }
            
            // For multi-depot, demands array includes demands for all locations (depots + demand points)
            problemData.demands = [
              ...selectedDepots.map(() => 0), // 0 demand for each depot
              ...selectedDemands.map(d => d.demand || 1) // demand for each demand point
            ];
            
            console.log('Multi-depot problem data:', {
              depots: problemData.depots,
              locations: problemData.locations,
              demands: problemData.demands,
              depotNames: problemData.depotNames,
              demandNames: problemData.demandNames,
              selectedDepots: selectedDepots.map(d => ({ name: d.name })),
              selectedDemands: selectedDemands.map(d => ({ name: d.name, demand: d.demand }))
            });
            
            problemData.depotNames = selectedDepots.map(d => d.name);
            problemData.demandNames = selectedDemands.map(d => d.name);
            
            // Add time windows from moventa.locations
            problemData.time_windows = this.extractTimeWindows(selectedDepots, selectedDemands);

            // Store metadata for proper solution interpretation
            problemData.depotCount = selectedDepots.length;
            problemData.demandCount = selectedDemands.length;
          }
          }
        }
        
        // Validate the problem data before solving
        const validation = this.validateProblemData(problemData);
        if (!validation.isValid) {
          this.error = validation.error;
          this.isLoading = false;
          return;
        }
        
        // Solve the problem using the appropriate solver
        console.log('Solving with solver type:', this.solverType);
        console.log('Problem data:', problemData);

        // Save the problem data before solving
        try {
            await RoutingService.saveProblem({ 
                ...problemData,
                timestamp: new Date().toISOString(),
                problemType: this.solverType
            });
            console.log('Problem data saved successfully.');
        } catch (saveError) {
            console.error('Failed to save problem data:', saveError);
            // Optional: Decide if you want to proceed even if saving fails
        }
        
        if (this.solverType === 'TSP') {
          this.solution = await RoutingService.solveTSP(problemData);
        } else if (this.solverType === 'LAP') {
          this.solution = await RoutingService.solveLAP(problemData);
        } else {
          this.solution = await RoutingService.solveVRP(problemData);
        }
        
        // Add locations to the solution for reference
        if (this.locationSource === 'database' || this.locationSource === 'collections') {
          const selectedDepots = this.depotLocations.filter(d => 
            this.selectedDepotIds.includes(d.id)
          );
          const selectedDemands = this.demandLocations.filter(d => 
            this.selectedDemandIds.includes(d.id)
          );
          
          if (this.solverType === 'TSP') {
            // Store all locations (depots + demand points) for map display
            this.solution.locations = [
              ...selectedDepots.map(d => d.coordinates),
              ...selectedDemands.map(d => d.coordinates)
            ];
            
            // Store location names for better display
            this.solution.locationNames = [
              ...selectedDepots.map(d => d.name),
              ...selectedDemands.map(d => d.name)
            ];
            
            this.solution.depotCount = selectedDepots.length;
            this.solution.demandCount = selectedDemands.length;
            this.solution.multiDepot = selectedDepots.length > 1;
          } else {
            // Store all locations in the solution for map display
            this.solution.locations = [
              ...selectedDepots.map(d => d.coordinates),
              ...selectedDemands.map(d => d.coordinates)
            ];
            
            // Store location names for better display
            this.solution.locationNames = [
              ...selectedDepots.map(d => d.name),
              ...selectedDemands.map(d => d.name)
            ];
            
            // Store depot count for proper indexing
            this.solution.depotCount = selectedDepots.length;
            this.solution.demandCount = selectedDemands.length;
            
            // Mark if this is a multi-depot solution
            this.solution.multiDepot = selectedDepots.length > 1;
          }
        }
        
        // Generate colors for routes (not needed for LAP)
        if (this.solverType !== 'LAP') {
          this.routeColors = RoutingService.getRouteColors(this.solution.routes.length);
        }
        
        // Emit the solution for map visualization
        this.$emit('solution-found', {
          solution: this.solution,
          allLocations: this.allLocations,
          routeColors: this.routeColors,
          solverType: this.solverType
        });
      } catch (err) {
        this.error = `Error: ${err.message || 'Failed to solve routing problem'}`;
        console.error('Routing error:', err);
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * Convert a stop index to a human-readable label
     */
    stopToLabel(stopIndex) {
      if (this.locationSource === 'database' && this.solution && this.solution.locationNames) {
        return this.solution.locationNames[stopIndex] || `Stop ${stopIndex}`;
      } else {
        if (stopIndex === 0) return 'Depot';
        return `Stop ${stopIndex}`;
      }
    },
    
    /**
     * Get vehicle information for display in the solution
     */
    getVehicleInfo(vehicleIndex) {
      if (!this.solution || !this.solution.vehicleIds) {
        return `Vehicle ${vehicleIndex + 1}`;
      }
      
      const vehicleId = this.solution.vehicleIds[vehicleIndex];
      const vehicle = this.availableVehicles.find(v => v.id === vehicleId);
      
      if (vehicle) {
        return `${vehicle.plate} (${vehicle.type})`;
      }
      
      return `Vehicle ${vehicleIndex + 1}`;
    },
    
    /**
     * Get vehicle capacity for display in the solution
     */
    getVehicleCapacity(vehicleId) {
      const vehicle = this.availableVehicles.find(v => v.id === vehicleId);
      return vehicle ? `Capacity: ${vehicle.capacity}` : '';
    },
    
    /**
     * Get facility name for LAP solution display
     */
    getFacilityName(facilityIdx) {
      if (this.solution && this.solution.facilities && this.solution.facilities[facilityIdx]) {
        return this.solution.facilities[facilityIdx].name || `Facility ${facilityIdx + 1}`;
      }
      
      // Fallback to depot locations if facilities aren't in solution
      if (this.depotLocations && this.depotLocations[facilityIdx]) {
        return this.depotLocations[facilityIdx].name;
      }
      
      return `Facility ${facilityIdx + 1}`;
    },

    /**
     * Get facility color for LAP solution display
     */
    getFacilityColor(facilityIdx) {
      const colors = [
        '#4285F4', '#34A853', '#FBBC05', '#EA4335', '#9C27B0',
        '#FF9800', '#795548', '#607D8B', '#E91E63', '#00BCD4'
      ];
      return colors[facilityIdx % colors.length];
    },
    
    /**
     * Open the save route dialog
     */
    saveRouteDialog() {
      this.saveRouteName = '';
      this.saveRouteError = '';
      this.saveRouteSuccess = '';
      this.showSaveDialog = true;
    },
    
    /**
     * Cancel saving the route
     */
    cancelSaveRoute() {
      this.showSaveDialog = false;
      this.saveRouteName = '';
      this.saveRouteError = '';
    },
    
    /**
     * Confirm and save the route
     */
    async confirmSaveRoute() {
      if (!this.saveRouteName.trim()) {
        this.saveRouteError = 'Please enter a route name.';
        return;
      }
      this.saveRouteError = '';
      this.saveRouteSuccess = '';
      try {
        // Prepare the route data in the required format
        const routeData = {
          ...this.solution,
          name: this.saveRouteName.trim(),
          timestamp: new Date().toISOString(),
        };
        // Save to backend (MongoDB)
        const result = await RoutingService.saveRoute(routeData);
        if (result.status === 'success') {
          this.saveRouteSuccess = 'Route saved successfully!';
          this.showSaveDialog = false;
        } else {
          this.saveRouteError = result.message || 'Failed to save route.';
        }
      } catch (err) {
        this.saveRouteError = err.message || 'Failed to save route.';
      }
    },
    
    /**
     * Add selected locations from map polygon/rectangle selection
     */
    addSelectedLocations(locations) {
      if (!locations || locations.length === 0) {
        return;
      }
      
      // Switch to database mode and auto-select the locations
      // If we have collection-based locations, stay with collections; otherwise use database
      if (this.selectedCollectionId) {
        this.locationSource = 'collections';
      } else {
        this.locationSource = 'database';
      }
      
      // Extract depot and demand locations from the selection
      const depotIds = [];
      const demandIds = [];
      
      locations.forEach(location => {
        if (location.type === 'depot') {
          depotIds.push(location.id || location._id);
        } else {
          demandIds.push(location.id || location._id);
        }
      });
      
      // Update selected locations
      this.selectedDepotIds = [...new Set([...this.selectedDepotIds, ...depotIds])];
      this.selectedDemandIds = [...new Set([...this.selectedDemandIds, ...demandIds])];
      
      console.log('Added selected locations:', {
        depots: depotIds.length,
        demands: demandIds.length,
        totalSelected: locations.length
      });
    },
    
    /**
     * Extract time windows from selected depot and demand locations
     */
    extractTimeWindows(selectedDepots, selectedDemands) {
      const timeWindows = [];
      
      // Add time windows for depots first
      selectedDepots.forEach(depot => {
        const timeWindow = this.getLocationTimeWindow(depot);
        timeWindows.push(timeWindow);
      });
      
      // Add time windows for demand locations
      selectedDemands.forEach(demand => {
        const timeWindow = this.getLocationTimeWindow(demand);
        timeWindows.push(timeWindow);
      });
      
      console.log('Extracted time windows:', {
        depots: selectedDepots.length,
        demands: selectedDemands.length,
        timeWindows: timeWindows.length,
        windows: timeWindows
      });
      
      return timeWindows;
    },
    
    /**
     * Get time window for a specific location from moventa.locations
     */
    getLocationTimeWindow(location) {
      const toSeconds = (val) => {
        if (!val && val !== 0) return null;
        const str = String(val).trim();
        if (str.includes(':')) {
          const [h, m] = str.split(':').map(Number);
          return h * 3600 + (m || 0) * 60;
        }
        const n = parseFloat(str);
        return isNaN(n) ? null : (n >= 3600 ? n : n * 3600);
      };

      const start = toSeconds(location.timeWindowStart);
      const end = toSeconds(location.timeWindowEnd);

      if (start !== null && end !== null) {
        return [start, end];
      }

      // Default: full day in seconds
      return [0, 86400];
    },
    
    /**
     * Calculate duration matrix between locations using OSRM or distance estimation
     */
    async calculateDurationMatrix(locations) {
      const n = locations.length;
      const matrix = Array(n).fill(0).map(() => Array(n).fill(0));
      
      try {
        // For small sets, we can use OSRM directly
        // For larger sets, we might need to batch or use distance estimation
        
        if (n <= 25) {
          // Use OSRM for accurate travel times
          const coordinates = locations.map(loc => loc.coordinates);
          const osrmUrl = `http://router.project-osrm.org/table/v1/driving/${coordinates.map(coord => coord.join(',')).join(';')}?sources=0;${Array.from({length: n-1}, (_, i) => i+1).join(';')}&destinations=0;${Array.from({length: n-1}, (_, i) => i+1).join(';')}`;
          
          const response = await fetch(osrmUrl);
          const data = await response.json();
          
          if (data.durations) {
            for (let i = 0; i < n; i++) {
              for (let j = 0; j < n; j++) {
                matrix[i][j] = Math.round(data.durations[i][j] || 0);
              }
            }
          }
        } else {
          // Fall back to distance-based estimation for larger sets
          for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
              if (i === j) {
                matrix[i][j] = 0;
              } else {
                const distance = this.calculateDistance(locations[i].coordinates, locations[j].coordinates);
                // Estimate travel time: distance(km) / 50 km/h * 3600 seconds/hour
                matrix[i][j] = Math.round((distance / 50) * 3600);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error calculating duration matrix:', error);
        // Fall back to distance-based estimation
        for (let i = 0; i < n; i++) {
          for (let j = 0; j < n; j++) {
            if (i === j) {
              matrix[i][j] = 0;
            } else {
              const distance = this.calculateDistance(locations[i].coordinates, locations[j].coordinates);
              // Estimate travel time: distance(km) / 50 km/h * 3600 seconds/hour
              matrix[i][j] = Math.round((distance / 50) * 3600);
            }
          }
        }
      }
      
      console.log('Duration matrix calculated:', {
        size: `${n}x${n}`,
        sampleTimes: matrix.slice(0, 3).map(row => row.slice(0, 3))
      });
      
      return matrix;
    },
    
    /**
     * Calculate distance between two coordinates in kilometers
     */
    calculateDistance([lon1, lat1], [lon2, lat2]) {
      const R = 6371; // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },
    
    /**
     * Clear all selected locations (reset selection)
     */
    clearSelectedLocations() {
      console.log('Clearing selected locations in RoutingPanel');
      this.selectedDepotIds = [];
      this.selectedDemandIds = [];
      this.selectedVehicleIds = [];
      
      // Clear solution and problem data to prevent persistence
      this.solution = null;
      this.error = null;
      this.routeColors = [];
      
      // Emit solution cleared event to update map
      this.$emit('solution-found', {
        solution: null,
        allLocations: [],
        routeColors: [],
        solverType: this.solverType
      });
    },
    
    /**
     * Handle boundary selection for routing context
     */
    handleBoundarySelected(boundary) {
      console.log('Boundary selected in RoutingPanel:', boundary);
      // Could be used to show boundary info in routing panel UI if needed
      // For now, just log it - the main functionality is in the location filtering
    },
  }
}
</script>

<style scoped>
.routing-panel {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  padding: 1.5rem;
  color: white;
}

.routing-panel h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.location-source-toggle {
  display: flex;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 0.25rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.location-source-toggle button {
  padding: 0.75rem 0.5rem;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  line-height: 1;
}

.location-source-toggle button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.location-source-toggle button.active {
  background: linear-gradient(135deg, #4285F4 0%, #34A853 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  transform: translateY(-1px);
}

.location-source-toggle button.active:hover {
  background: linear-gradient(135deg, #3367D6 0%, #2E8B47 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(66, 133, 244, 0.4);
}

.solver-type-toggle {
  display: flex;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 0.25rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.solver-type-toggle button {
  padding: 0.75rem 1rem;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 0;
  font-size: 0.9rem;
  text-align: center;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.8rem;
  line-height: 1;
}

.solver-type-toggle button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.solver-type-toggle button.active {
  background: linear-gradient(135deg, #FF6B35 0%, #F7931E 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  transform: translateY(-1px);
}

.solver-type-toggle button.active:hover {
  background: linear-gradient(135deg, #E5522A 0%, #E0821A 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 53, 0.4);
}

.database-locations {
  margin-bottom: 1rem;
}

/* Modern Vehicle Configuration Styles */
.modern-vehicle-config {
  background: linear-gradient(135deg, rgba(66, 133, 244, 0.15) 0%, rgba(52, 168, 83, 0.15) 100%);
  border: 2px solid rgba(66, 133, 244, 0.3);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 100%;
  box-sizing: border-box;
}

.modern-vehicle-config::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #4285F4, #34A853, #FBBC05, #EA4335);
  border-radius: 12px 12px 0 0;
}

.config-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.config-header h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.config-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}

.config-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%;
}

.config-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.modern-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  transition: all 0.3s ease;
  position: relative;
  min-height: 48px;
  width: 100%;
  box-sizing: border-box;
}

.modern-input-wrapper:focus-within {
  border-color: #4285F4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.2);
  background: rgba(255, 255, 255, 0.25);
}

.modern-input {
  flex: 1;
  padding: 0.875rem 1.25rem;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  outline: none;
  width: 100%;
  text-align: center;
  min-width: 60px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.modern-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  text-shadow: none;
}

.modern-input::-webkit-outer-spin-button,
.modern-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.modern-input[type=number] {
  -moz-appearance: textfield;
}

.input-suffix {
  padding: 0.875rem 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 600;
  border-left: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.modern-help {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-style: italic;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.depot-selection, .demand-selection, .vehicles-selection {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 0.5rem;
}

.depot-checkbox, .demand-checkbox, .vehicle-checkbox {
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
}

.depot-checkbox:hover, .demand-checkbox:hover, .vehicle-checkbox:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.depot-checkbox input, .demand-checkbox input, .vehicle-checkbox input {
  width: auto;
  margin-right: 8px;
}

.depot-checkbox label, .demand-checkbox label, .vehicle-checkbox label {
  margin: 0;
  font-size: 0.9rem;
  display: inline;
  cursor: pointer;
  font-weight: normal;
}

.demand-value {
  color: #ccc;
  font-size: 0.8rem;
}

.loading-message, .empty-message {
  padding: 1rem;
  text-align: center;
  color: #ccc;
  font-style: italic;
}

.locations-list h4 {
  margin: 0 0 0.5rem 0;
}

.location-item {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 4px;
}

.location-coords {
  font-size: 0.8rem;
  color: #ccc;
}

.location-demand {
  margin-top: 0.5rem;
}

.small-input {
  width: 60px;
  padding: 0.25rem;
  margin-left: 0.5rem;
}

.remove-btn {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.remove-btn:hover {
  background-color: #c0392b;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.primary-btn, .secondary-btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
}

.primary-btn {
  background-color: #4ecdc4;
  color: white;
  flex: 1;
}

.primary-btn:hover:not(:disabled) {
  background-color: #3dbdb5;
}

.primary-btn:disabled {
  background-color: #555;
  cursor: not-allowed;
}

.secondary-btn {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.secondary-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.loading {
  text-align: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  margin-top: 1rem;
}

.error-message {
  background-color: rgba(231, 76, 60, 0.2);
  color: #ff6b6b;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  border: 1px solid rgba(231, 76, 60, 0.3);
}

.solution-summary {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

.solution-summary h4 {
  margin: 0 0 0.5rem 0;
  color: #4ecdc4;
}

.route-item {
  background-color: rgba(255, 255, 255, 0.05);
  margin: 0.5rem 0;
  border-radius: 4px;
  overflow: hidden;
}

.route-header {
  padding: 0.5rem;
  color: white;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.route-capacity, .route-duration {
  font-size: 0.8rem;
  opacity: 0.9;
}

.route-stops {
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.02);
}

.route-stop {
  display: inline-block;
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.route-arrow {
  color: #ccc;
  margin: 0 0.25rem;
}

.save-route-dialog {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.save-route-dialog .dialog-content {
  background: #fff;
  padding: 2rem;
  border-radius: 10px;
  min-width: 320px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.save-route-dialog input {
  width: 100%;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #ccc;
}

.dialog-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.success-message {
  color: #43e97b;
  margin-top: 0.5rem;
}

.error-message {
  color: #fa709a;
  margin-top: 0.5rem;
}

/* Selected item styling */
.selected-item {
  background-color: rgba(52, 152, 219, 0.15) !important;
  border: 1px solid rgba(52, 152, 219, 0.3);
  transition: all 0.3s ease;
}

.selected-item:hover {
  background-color: rgba(52, 152, 219, 0.25) !important;
}

.selected-indicator {
  color: #2ecc71;
  font-weight: bold;
  margin-right: 8px;
  font-size: 14px;
}

.collection-selection {
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.collection-dropdown {
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.collection-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.collection-dropdown:focus {
  outline: none;
  border-color: #4285F4;
  box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
}

.collection-dropdown option {
  background-color: #2c3e50;
  color: white;
  padding: 0.5rem;
}

/* Responsive design for location source toggle */
@media (max-width: 400px) {
  .location-source-toggle {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .location-source-toggle button {
    flex: none;
    width: 100%;
    margin: 0;
    font-size: 0.8rem;
    padding: 0.75rem 1rem;
  }
}

/* For very narrow panels */
@media (max-width: 320px) {
  .location-source-toggle button {
    font-size: 0.7rem;
    letter-spacing: 0.2px;
    padding: 0.6rem 0.4rem;
  }
}

/* Additional styling for better visual hierarchy */
.location-source-toggle button:first-child {
  margin-left: 0;
}

.location-source-toggle button:last-child {
  margin-right: 0;
}

.location-source-toggle button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.5);
}

/* LAP Solution Display Styles */
.lap-solution {
  margin-top: 1rem;
}

.solution-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.metric-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.metric-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
}

.metric-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #4ecdc4;
}

.facility-utilization {
  margin-top: 1rem;
}

.facility-utilization h5 {
  margin: 0 0 1rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.utilization-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.utilization-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.1);
}

.facility-name {
  min-width: 120px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.utilization-bar {
  flex: 1;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.utilization-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s ease-in-out;
  position: relative;
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.1) 0%, 
    currentColor 50%, 
    rgba(255, 255, 255, 0.1) 100%);
}

.utilization-text {
  min-width: 100px;
  text-align: right;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* LAP-specific form styles */
.lap-options {
  background-color: rgba(138, 43, 226, 0.1);
  border: 1px solid rgba(138, 43, 226, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.objective-dropdown, .distance-input, .cost-input {
  width: 100%;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.objective-dropdown:hover, .distance-input:hover, .cost-input:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.objective-dropdown:focus, .distance-input:focus, .cost-input:focus {
  outline: none;
  border-color: #8A2BE2;
  box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.3);
}

.objective-dropdown option {
  background-color: #2c3e50;
  color: white;
}

.capacity-value {
  color: #8A2BE2;
  font-weight: 500;
  font-size: 0.85rem;
}

/* Responsive design for LAP solution */
@media (max-width: 600px) {
  .solution-metrics {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .utilization-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .facility-name {
    min-width: auto;
    text-align: center;
  }
  
  .utilization-text {
    min-width: auto;
    text-align: center;
  }
}

/* Simplified Mode Styles */
.simplified-mode {
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.service-personnel-input {
  width: 100%;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.service-personnel-input:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.service-personnel-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
}

.input-help {
  display: block;
  margin-top: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  font-style: italic;
}
</style>
